import { Button, TextInput } from '@mantine/core'
import { useForm } from '@mantine/form'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { getConfig } from '@/config.ts'
import { getRecaptcha, initRecaptcha, useMedplum } from '@medplum/react'
import { showNotification } from '@mantine/notifications'
import { useEffect, useState } from 'react'
import { useAppFlags } from '@/hooks/useAppFlags'
import { useClient } from '@/hooks/useClient'
import { AuthCardLayout } from './components/AuthCardLayout'
import {
  AUTH_CLASSES,
  AUTH_LOGIN_PRIMARY_BUTTON_STYLES,
  AUTH_LOGIN_TEXT_INPUT_STYLES,
} from './authTheme'

export function ForgotPassword() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const fromMobileApp = searchParams.get('source') === 'mobile'
  const medplum = useMedplum()
  const client = useClient()
  const { authV2Provider } = useAppFlags<{ authV2Provider?: boolean }>()

  const form = useForm({
    initialValues: {
      email: '',
    },
    validate: {
      email: (value) => (/^\S+@\S+$/.test(value) ? null : 'Invalid email'),
    },
  })

  const [recaptchaSiteKey, setRecaptchaSiteKey] = useState<string | undefined>()
  /** Mobile-app flow only: the post-submit "check your email" confirmation. */
  const [emailSent, setEmailSent] = useState(false)

  useEffect(() => {
    setRecaptchaSiteKey(getConfig().recaptchaSiteKey)
  }, [])

  useEffect(() => {
    if (recaptchaSiteKey) {
      initRecaptcha(recaptchaSiteKey)
    }
  }, [recaptchaSiteKey])

  const handleSubmit = async (formData: Record<string, string>) => {
    let recaptchaToken = ''
    if (recaptchaSiteKey) {
      recaptchaToken = await getRecaptcha(recaptchaSiteKey)
    }

    // Gateway flow (PD-1512): same request, proxied server-side; Medplum still
    // validates the recaptcha token (single-use, passed through untouched).
    const request = authV2Provider
      ? client.authResetPassword({ email: formData.email, recaptchaToken })
      : medplum.post('auth/resetpassword', { ...formData, recaptchaToken })

    request
      .then(() => {
        if (fromMobileApp) {
          // App users have no web login to land on — hold them here with instructions.
          setEmailSent(true)
          return
        }
        showNotification({
          color: 'green',
          message: 'Please check your email for instructions to reset your password',
        })
        void navigate('/login')
      })
      .catch((err) =>
        showNotification({
          color: 'red',
          message: err.message || 'Something went wrong',
        }),
      )
  }

  return (
    <AuthCardLayout
      title="Reset your password"
      description={
        emailSent
          ? undefined
          : "Enter your work email. If an account exists with it, we'll send you a password reset link."
      }
    >
      {emailSent && (
        <p className={`m-0 text-[14px] leading-relaxed ${AUTH_CLASSES.secondaryText}`}>
          If an account exists with this email, we&apos;ve sent a password reset link. Open the
          email and follow the instructions, then return to the beHuman Pro app and sign in with
          your new password.
        </p>
      )}

      {!emailSent && (
        <form
          className="flex flex-col gap-4"
          onSubmit={form.onSubmit((values) => handleSubmit(values))}
        >
          <TextInput
            label="Work email"
            type="email"
            placeholder="you@practice.com"
            autoComplete="email"
            styles={AUTH_LOGIN_TEXT_INPUT_STYLES}
            // Sub-16px inputs make iOS Safari auto-zoom the page on focus.
            classNames={{ input: `${AUTH_CLASSES.inputFocus} max-md:!text-[16px]` }}
            {...form.getInputProps('email')}
          />

          <Button
            disabled={!form.values.email}
            type="submit"
            loading={form.submitting}
            fullWidth
            size="md"
            styles={AUTH_LOGIN_PRIMARY_BUTTON_STYLES}
            classNames={{ root: AUTH_CLASSES.primaryButtonState }}
          >
            Send reset link
          </Button>

          {/* The mobile app links here with ?source=mobile — its users have no web
            login to go back to, so the link is hidden for them. */}
          {!fromMobileApp && (
            <Link
              to="/login"
              className={`mt-2 self-center text-[14px] font-medium ${AUTH_CLASSES.accentLink}`}
            >
              Back to login
            </Link>
          )}
        </form>
      )}
    </AuthCardLayout>
  )
}
